import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { ProfileLink } from "@/components/ProfileDrawer";
import { toast } from "sonner";
import { Plus, X, Trash2, Edit3 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const empty = { name: "", rc_number: "", contact_phone: "+234 ", contact_email: "", address: "", notes: "" };

export const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const r = await api.get("/clients");
    setClients(r.data);
    const map = {};
    await Promise.all(r.data.map(async (c) => {
      try {
        const r2 = await api.get(`/clients/${c.id}`);
        map[c.id] = r2.data;
      } catch (err) {
        console.error(`Failed to load client ${c.id}:`, err);
      }
    }));
    setStats(map);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name || "", rc_number: c.rc_number || "",
      contact_phone: c.contact_phone || "+234 ", contact_email: c.contact_email || "",
      address: c.address || "", notes: c.notes || "",
    });
    setOpen(true);
  };
  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/clients/${editing}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}? This deletes their sites, users, contracts and invoices.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Only super_admin can delete clients");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-muted" style={{ fontSize: 14 }}>{clients.length} clients on file</div>
        <button className="btn-primary inline-flex items-center gap-2" onClick={startNew} data-testid="add-client-btn">
          <Plus size={14} /> Add client
        </button>
      </div>

      <ClientsTable
        clients={clients}
        stats={stats}
        canDelete={user?.role === "super_admin"}
        onEdit={startEdit}
        onRemove={remove}
      />

      {open && (
        <ClientFormModal
          editing={editing}
          form={form}
          setForm={setForm}
          onClose={() => setOpen(false)}
          onSubmit={submit}
        />
      )}
    </div>
  );
};

const ClientsTable = ({ clients, stats, canDelete, onEdit, onRemove }) => (
  <div className="s-card" style={{ padding: 0 }}>
    <table className="s-table">
      <thead><tr><th>Client</th><th>RC</th><th>Contact</th><th>Sites</th><th>Outstanding</th><th></th></tr></thead>
      <tbody>
        {clients.map(c => (
          <ClientRow key={c.id} client={c} stat={stats[c.id]} canDelete={canDelete} onEdit={onEdit} onRemove={onRemove} />
        ))}
      </tbody>
    </table>
  </div>
);

const ClientRow = ({ client: c, stat, canDelete, onEdit, onRemove }) => {
  const outstanding = stat?.invoices_outstanding || 0;
  return (
    <tr data-testid={`client-row-${c.id}`}>
      <td><ProfileLink kind="client" id={c.id}>{c.name}</ProfileLink></td>
      <td className="mono text-muted">{c.rc_number}</td>
      <td className="text-muted" style={{ fontSize: 13 }}>{c.contact_email}<br />{c.contact_phone}</td>
      <td>{stat?.sites_count ?? "—"}</td>
      <td className="mono" style={{ color: outstanding > 0 ? "#E63946" : "#8CA0B8" }}>
        ₦{outstanding.toLocaleString("en-NG")}
      </td>
      <td>
        <div className="flex gap-2">
          <button onClick={() => onEdit(c)} className="text-muted" title="Edit" data-testid={`edit-client-${c.id}`}>
            <Edit3 size={14} />
          </button>
          {canDelete && (
            <button onClick={() => onRemove(c)} className="text-alert" title="Delete" data-testid={`del-client-${c.id}`}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const ClientFormModal = ({ editing, form, setForm, onClose, onSubmit }) => (
  <div onClick={onClose} style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex",
    alignItems: "center", justifyContent: "center", zIndex: 100,
  }}>
    <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="s-card" style={{ width: 480, maxWidth: "90vw" }}>
      <div className="flex justify-between items-center mb-4">
        <div className="h-section">{editing ? "Edit client" : "Add client"}</div>
        <button type="button" onClick={onClose} className="text-muted"><X size={18} /></button>
      </div>
      <Lbl>Company name</Lbl>
      <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="s-input mt-1 mb-3" data-testid="c-name" />
      <Lbl>RC number</Lbl>
      <input value={form.rc_number} onChange={(e) => setForm({...form, rc_number: e.target.value})} className="s-input mt-1 mb-3 mono" data-testid="c-rc" />
      <Lbl>Contact phone (+234)</Lbl>
      <input value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} className="s-input mt-1 mb-3" data-testid="c-phone" />
      <Lbl>Contact email</Lbl>
      <input type="email" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} className="s-input mt-1 mb-3" data-testid="c-email" />
      <Lbl>Address</Lbl>
      <input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="s-input mt-1 mb-3" />
      <Lbl>Notes</Lbl>
      <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="s-input mt-1 mb-4" rows={2} />
      <button type="submit" className="btn-primary w-full" data-testid="c-submit">{editing ? "Save changes" : "Add client"}</button>
    </form>
  </div>
);

const Lbl = ({ children }) => <label className="text-muted" style={{ fontSize: 12 }}>{children}</label>;
